import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { ContentCourse } from "../../schemas/schemas";

interface CourseSettingsProps {
  courseData: ContentCourse;
  t: (key: string) => string;
}

export function CourseSettings({ courseData, t }: CourseSettingsProps) {
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('course-content.composer.settings')}</CardTitle>
        <CardDescription>{t('course-content.composer.settingsDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('course-content.composer.courseStatusLabel')}
              </label>
              <select
                id="status"
                defaultValue={courseData.status || 'draft'}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              >
                <option value="draft">{t('dashboard.contentStatus.draft')}</option>
                <option value="pending_approval">{t('dashboard.contentStatus.pendingApproval')}</option>
                <option value="published">{t('dashboard.contentStatus.published')}</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('course-content.composer.categoryLabel')}
              </label>
              <select
                id="category"
                defaultValue={courseData.category || ''}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              >
                <option value="">Select Category</option>
                <option value="Mathematics">Mathematics</option>
                <option value="Science">Science</option>
                <option value="Computer Science">Computer Science</option>
                <option value="History">History</option>
                <option value="Language Arts">Language Arts</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="level"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('course-content.composer.levelLabel')}
              </label>
              <select
                id="level"
                defaultValue={courseData.level || ''}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              >
                <option value="">Select Level</option>
                <option value="beginner">{t('course-content.composer.beginnerLevel')}</option>
                <option value="intermediate">{t('course-content.composer.intermediateLevel')}</option>
                <option value="advanced">{t('course-content.composer.advancedLevel')}</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="duration"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t('course-content.composer.durationLabel')} (hours)
              </label>
              <input
                type="number"
                id="duration"
                defaultValue={courseData.estimated_hours || courseData.duration || 0}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                min="0"
                step="0.5"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="tags"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('course-content.composer.tagsLabel')}
            </label>
            <input
              type="text"
              id="tags"
              defaultValue={courseData.tags?.join(", ") || ''}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              placeholder={t('course-content.composer.tagsPlaceholder')}
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('course-content.composer.courseDescriptionLabel')}
            </label>
            <textarea
              id="description"
              rows={3}
              defaultValue={courseData.description || ''}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
            />
          </div>

          <div className="flex items-center">
            <input
              id="required"
              name="required"
              type="checkbox"
              defaultChecked={courseData.requiredForCompletion || false}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="required"
              className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
            >
              {t('course-content.composer.requiredForCompletion')}
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="sequential"
              name="sequential"
              type="checkbox"
              defaultChecked={courseData.sequentialProgress || false}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="sequential"
              className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
            >
              {t('course-content.composer.sequentialProgress')}
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="course_type"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Course Type
              </label>
              <select
                id="course_type"
                defaultValue={courseData.course_type || 'e-learning'}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
              >
                <option value="e-learning">E-Learning</option>
                <option value="live-online">Live Online</option>
                <option value="offline">Offline</option>
                <option value="combined">Combined</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="weeks"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Duration (weeks)
              </label>
              <input
                type="number"
                id="weeks"
                defaultValue={courseData.weeks || 0}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                min="0"
                step="1"
              />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline">{t('course-content.composer.cancel')}</Button>
        <Button variant="primary">{t('course-content.composer.save')}</Button>
      </CardFooter>
    </Card>
  );
} 